import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { errorDetail } from '@parsina/framework/clinical/src/lib/appError';
import { useState, useCallback } from 'react';
import { bffMutation } from '@parsina/framework/clinical/src/lib/bffClient';
import { coinPriceToman, creditPrice } from '@parsina/framework/clinical/src/lib/useCreditPrices';
import { PAYMENT_GATEWAY, toGatewayAmount, hasRailFor } from '../constants/paymentGateway';

// Starts a top-up: asks the BFF for a payment URL, then redirects the browser to
// the gateway. On return, the wallet callback credits the coins and the balance
// refetches (visibility/nav-based). The UI works in Toman throughout; each
// gateway's wire unit is applied here (Sepehr settles in rials, Zarinpal toman).
const INITIATE_QUERY = `
  mutation InitiateTopUp($gateway: String!, $ownerId: String!, $ownerType: String!, $amount: Int!, $description: String, $returnUrl: String) {
    initiateGatewayPayment(gateway: $gateway, ownerId: $ownerId, ownerType: $ownerType, amount: $amount, description: $description, returnUrl: $returnUrl) {
      gateway
      authority
      paymentUrl
      invoiceId
    }
  }
`;

// An in-app path, and nothing else. A returnUrl is a redirect the gateway
// performs on our behalf, so an absolute URL here would be an open redirect
// with a bank in the middle of it. The query is dropped too: the gateway
// appends `?payment=…` itself, and not all of them accept a return URL that
// already carries one.
const inAppReturnPath = (path) =>
  typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')
    ? path.split('?')[0]
    : '/credits';

/**
 * @param returnPath where the BROWSER comes back to after the gateway, as an
 *   in-app path. Defaults to `/credits`, the screen that reports the outcome
 *   and refreshes the ledger.
 *
 *   It is a parameter rather than a constant because a top-up begun somewhere
 *   else should come back there — a doctor who tops up mid-visit wants the
 *   visit, not the credits screen. Wherever it lands the shell reports the
 *   outcome (App.jsx `usePaymentOutcomeToast`), so the toast is no longer the
 *   /credits screen's privilege.
 */
export function useTopUp(doctorId, gateway = PAYMENT_GATEWAY, { returnPath = '/credits' } = {}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const startTopUp = useCallback(async (coins, toman) => {
    if (!doctorId) return;
    // The gateways here take a toman price. Credit priced in another currency has
    // no rail in this panel (constants/paymentGateway.js hasRailFor): say so
    // rather than hand an Iranian bank a dollar figure. No entry point offers
    // this today — it is the last line, not the first.
    if (!hasRailFor(creditPrice().currency)) {
      setError(t('credits.gatewayUnavailable'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const amountToman = Math.round(toman ?? coins * coinPriceToman());
      const res = await bffMutation(INITIATE_QUERY, {
        gateway,
        ownerId: doctorId,
        ownerType: 'doctor',
        amount: toGatewayAmount(amountToman, gateway),
        description: t('credits.topUpDescription', { coins }),
        // Where the BROWSER returns. The payment BFF settles the payment with
        // the bank and then sends the doctor back here — this app never sees
        // the gateway's callback, and could not settle one if it did.
        returnUrl: `${window.location.origin}${inAppReturnPath(returnPath)}`,
      });
      const url = res?.initiateGatewayPayment?.paymentUrl;
      if (url) {
        window.location.href = url;
        return;
      }
      setError(t('credits.gatewayUnavailable'));
    } catch (e) {
      // Every failure used to be reported as "coming soon — call support",
      // which is wrong twice over: it says a working feature does not exist,
      // and it hides what actually went wrong. A gateway that refuses the
      // amount, a wallet that is down and a network blip all read identically,
      // so nobody could tell a misconfiguration from an outage. Surface the
      // server's own message when there is one — but only that: `errorDetail`
      // drops transport noise («Failed to fetch») the doctor cannot act on and
      // keeps what the server wrote for people (DC §2.4).
      const base = t('credits.paymentNotStarted');
      const detail = errorDetail(e)?.trim();
      setError(detail ? `${base} (${detail})` : base);
    } finally {
      setBusy(false);
    }
  }, [doctorId, gateway, returnPath, t]);

  return { startTopUp, busy, error };
}
